import {css, StyleSheet} from 'aphrodite';
import classNames from 'classnames';
import React from 'react';
import _ from 'underscore';

import {assert} from 'Asserts';
import {OBJECT_TYPE_TO_REMOVED_CHILD_CLASS} from 'config-migration/import/ObjectSectionConfig';
import {HighImpactChange} from 'config-migration/MigrationConstants';
import {HighImpactChangeIcon} from 'config-migration/shared-ui-components/HighImpactChangeIcon';
import {CollapsibleTriangle} from 'config-migration/shared-ui-components/TriangleToggle';
import {
  BaseMigrationObjectPlan,
  PrimaryObjectPlan,
  SecondaryObjectPlan,
} from 'config-migration/types/MigrationObjects';
import {
  NESTED_CHILD_OBJECT_TYPES,
  PrimaryObjectType,
  SecondaryObjectType,
} from 'config-migration/types/ObjectType';
import getTestProps from 'test/lib/cypress/getTestProps';
import DataTestAttrs from 'test/lib/cypress/shared/DataTestAttrs';
import {GRAY_DARK, GRAY_LIGHT, GRAY_LIGHTER, WHITE} from 'ui-library/Colors';

import {ObjectActionType} from '../../types/objectActions';
import ObjectActionDropdown, {getActionDescription} from '../ObjectActionDropdown';
import NestedChildImportPlanRow from './NestedChildImportPlanRow';
import {
  BASE_ROW_PADDING,
  getSortedChildMigrationPlans,
  isForcedAutoUpdate,
  getNestedActionDropdownMargin,
  getNestedRowPadding,
  getPlanForUpdatedChildPlan,
  getChildSectionTitleText,
  NestedCardRowType,
} from './utils/childImportPlanUtils';

interface ChildImportPlanSectionProps {
  plan: PrimaryObjectPlan;
  childType: PrimaryObjectType | SecondaryObjectType;
  nestedDepth: number;
  onUpdatePlan: (plan: PrimaryObjectPlan) => void;
  highImpactChangeMap: Map<string, Array<HighImpactChange>>;
}

interface ChildImportPlanSectionWrapperProps {
  children: React.ReactNode;
  title: string;
  nestedDepth: number;
  nestedCardRowType: NestedCardRowType;
  dataTestAttrs?: DataTestAttrs;
}

export function ChildImportPlanSectionWrapper(props: ChildImportPlanSectionWrapperProps): JSX.Element {
  return (
    <div {...getTestProps(props.dataTestAttrs)}>
      <div className={classNames('simpleDivTable-row', css(styles.childRow))}>
        <div
          className={classNames(
            'simpleDivTable-cell',
            getNestedCardRowStyles(props.nestedCardRowType, true, true)
          )}
          style={{paddingLeft: `${getNestedRowPadding(props.nestedDepth)}px`}}
        >
          <strong>{props.title}</strong>
        </div>
      </div>
      {props.children}
    </div>
  );
}

export default function ChildImportPlanSection(props: ChildImportPlanSectionProps): JSX.Element {
  const relevantChildPlans = props.plan.childMigrationObjectsByType.get(props.childType);
  assert(relevantChildPlans !== undefined, `No child plans found for ${props.childType}`);
  const sortedChildMigrationPlans = getSortedChildMigrationPlans(relevantChildPlans.childMigrationPlans);

  const originalIndexMap = new Map(
    relevantChildPlans.childMigrationPlans.map((plan, index) => [plan.objectId, index])
  );
  return (
    <ChildImportPlanSectionWrapper
      title={getChildSectionTitleText(props.plan, props.childType)}
      nestedDepth={props.nestedDepth}
      nestedCardRowType={NestedCardRowType.NonNested}
    >
      {sortedChildMigrationPlans.map((child) =>
        NESTED_CHILD_OBJECT_TYPES.has(child.objectType) ? (
          <>
            <NestedChildImportPlanRow
              key={child.objectId}
              plan={props.plan}
              childPlan={child}
              onUpdatePlan={props.onUpdatePlan}
              nestedDepth={props.nestedDepth + 1}
              highImpactChangeMap={props.highImpactChangeMap}
              childOriginalIndex={originalIndexMap.get(child.objectId)}
            />
            <div className={classNames('simpleDivTable-row', css(styles.childRow))} />
          </>
        ) : (
          <ChildImportPlanRow
            key={child.objectId}
            plan={child}
            onUpdateChildPlan={(updatedChildPlan: SecondaryObjectPlan) => {
              props.onUpdatePlan(
                getPlanForUpdatedChildPlan(
                  props.plan,
                  relevantChildPlans,
                  updatedChildPlan,
                  originalIndexMap.get(child.objectId)
                ) as PrimaryObjectPlan
              );
            }}
            nestedDepth={props.nestedDepth}
            highImpactChanges={props.highImpactChangeMap.get(child.objectId) || []}
            nestedCardRowType={NestedCardRowType.NonNested}
            setIsCollapsed={null}
            isCollapsed={false}
            hasChildren={false}
          />
        )
      )}
      {getSubtractedRowsFromPlan(
        props.plan,
        props.childType,
        props.highImpactChangeMap,
        props.nestedDepth,
        NESTED_CHILD_OBJECT_TYPES.has(props.childType as SecondaryObjectType),
        true
      )}
      {getUnplannableRowsFromPlan(
        props.plan,
        props.childType,
        props.nestedDepth,
        NESTED_CHILD_OBJECT_TYPES.has(props.childType as SecondaryObjectType),
        true
      )}
      <div className={classNames('simpleDivTable-row', css(styles.childRow))} />
    </ChildImportPlanSectionWrapper>
  );
}

interface ChildImportPlanRowProps {
  plan: BaseMigrationObjectPlan;
  nestedDepth: number;
  onUpdateChildPlan: (plan: BaseMigrationObjectPlan) => void;
  highImpactChanges: Array<HighImpactChange>;
  nestedCardRowType: NestedCardRowType;
  setIsCollapsed: ((isCollapsed: boolean) => void) | null;
  isCollapsed: boolean;
  hasChildren: boolean;
}

export function ChildImportPlanRow(props: ChildImportPlanRowProps): JSX.Element {
  const forcedAutoUpdate = isForcedAutoUpdate(props.plan);
  return (
    <div
      className={classNames(
        'simpleDivTable-row',
        props.plan.selectedAction.type === ObjectActionType.IGNORE && css(styles.ignoreRow),
        forcedAutoUpdate && css(styles.forcedAutoUpdateRow),
        css(styles.childRow)
      )}
      key={props.plan.objectId}
    >
      <div
        className={classNames(
          'simpleDivTable-cell',
          'u-flex-display u-vertical-center-items',
          getNestedCardRowStyles(props.nestedCardRowType, true, false)
        )}
        style={{paddingLeft: `${getNestedRowPadding(props.nestedDepth)}px`}}
      >
        <span
          className={classNames(css(styles.descriptionText))}
          onClick={() => {
            if (props.setIsCollapsed != null) {
              props.setIsCollapsed(!props.isCollapsed);
            }
          }}
        >
          {props.hasChildren && <CollapsibleTriangle isCollapsed={props.isCollapsed} />}
          {props.nestedCardRowType === NestedCardRowType.Header ||
          props.nestedCardRowType === NestedCardRowType.HeaderAndFooter ? (
            <strong>{props.plan.objectDisplayName}</strong>
          ) : (
            props.plan.objectDisplayName
          )}
        </span>
        {props.highImpactChanges.map((highImpactChange) => (
          <HighImpactChangeIcon key={_.uniqueId('icon-')} highImpactChange={highImpactChange} />
        ))}
      </div>
      <div
        className={classNames(
          'simpleDivTable-cell',
          getNestedCardRowStyles(props.nestedCardRowType, false, true)
        )}
        style={{marginLeft: `${getNestedActionDropdownMargin(props.nestedDepth, props.nestedCardRowType)}px`}}
      >
        {forcedAutoUpdate ? (
          getActionDescription(props.plan.initialAction)
        ) : (
          <ObjectActionDropdown
            object={props.plan}
            onUpdateObject={props.onUpdateChildPlan}
            showIgnore={false}
            filteredAdditionalActions={[]}
          />
        )}
      </div>
    </div>
  );
}

function getSubtractedCardRowType(
  isNestedCard: boolean,
  index: number,
  subtractedLength: number,
  canBeHeader: boolean
): NestedCardRowType {
  if (!isNestedCard) {
    return NestedCardRowType.NonNested;
  }
  if (canBeHeader) {
    if (subtractedLength === 1) {
      return NestedCardRowType.HeaderAndFooter;
    }
    if (index === 0) {
      return NestedCardRowType.Header;
    }
  }
  if (index === subtractedLength - 1) {
    return NestedCardRowType.Footer;
  }
  return NestedCardRowType.Regular;
}

export function getSubtractedRowsFromPlan(
  plan: PrimaryObjectPlan | SecondaryObjectPlan,
  childType: PrimaryObjectType | SecondaryObjectType,
  highImpactChangeMap: Map<string, Array<HighImpactChange>>,
  nestedDepth: number,
  isNestedCard: boolean,
  canBeHeader: boolean = false
): JSX.Element {
  const relevantChildPlans = plan.childMigrationObjectsByType.get(childType);
  assert(relevantChildPlans !== undefined, `No child plans found for ${childType}`);
  const shouldRemove = OBJECT_TYPE_TO_REMOVED_CHILD_CLASS.has(plan.objectType);

  return (
    <>
      {relevantChildPlans?.subtractedChildObjects.map((childObject, index) => (
        <SubtractedChildToRow
          displayName={childObject.identifier.displayName}
          highImpactChanges={highImpactChangeMap.get(childObject.identifier.provenanceUri) || []}
          key={_.uniqueId('child-')}
          shouldRemove={shouldRemove}
          nestedDepth={nestedDepth}
          nestedCardRowType={getSubtractedCardRowType(
            isNestedCard,
            index,
            relevantChildPlans.subtractedChildObjects.length,
            canBeHeader
          )}
        />
      ))}
    </>
  );
}

interface SubtractedChildToRowProps {
  displayName: string;
  highImpactChanges: Array<HighImpactChange>;
  shouldRemove: boolean;
  nestedDepth: number;
  nestedCardRowType: NestedCardRowType;
}

/**
 * A row for a child object that is not found in child migration plans. This means that the child
 * was either removed or archived.
 */
function SubtractedChildToRow(props: SubtractedChildToRowProps): JSX.Element {
  return (
    <div className={classNames('simpleDivTable-row', css(styles.childRow))} key={props.displayName}>
      <div
        className={classNames(
          'simpleDivTable-cell',
          getNestedCardRowStyles(props.nestedCardRowType, true, false),
          props.nestedCardRowType !== NestedCardRowType.NonNested && css(styles.nestedCardEmpty)
        )}
        style={{paddingLeft: `${getNestedRowPadding(props.nestedDepth)}px`}}
      />
      <div
        className={classNames(
          'simpleDivTable-cell',
          getNestedCardRowStyles(props.nestedCardRowType, false, true)
        )}
        style={{
          marginLeft: `${getNestedActionDropdownMargin(props.nestedDepth, props.nestedCardRowType)}px`,
        }}
      >
        <span className={css(styles.descriptionText)}>
          {props.shouldRemove ? 'Remove' : 'Archive'} <strong>{props.displayName}</strong>
        </span>
        {props.highImpactChanges.map((highImpactChange) => (
          <HighImpactChangeIcon
            key={_.uniqueId('high-impact-missing-icon-')}
            highImpactChange={highImpactChange}
          />
        ))}
      </div>
    </div>
  );
}

export function getUnplannableRowsFromPlan(
  plan: PrimaryObjectPlan | SecondaryObjectPlan,
  childType: PrimaryObjectType | SecondaryObjectType,
  nestedDepth: number,
  isNestedCard: boolean,
  canBeHeader: boolean = false
): JSX.Element {
  const relevantChildPlans = plan.childMigrationObjectsByType.get(childType);
  assert(relevantChildPlans !== undefined, `No child plans found for ${childType}`);

  return (
    <>
      {relevantChildPlans?.unplannableChildObjects.map((childObject, index) => (
        <UnplannableChildRow
          key={_.uniqueId('child-')}
          displayName={childObject.displayName}
          nestedDepth={nestedDepth}
          nestedCardRowType={getSubtractedCardRowType(
            isNestedCard,
            index,
            relevantChildPlans.unplannableChildObjects.length,
            canBeHeader
          )}
        />
      ))}
    </>
  );
}

interface UnplannableChildRowProps {
  displayName: string;
  nestedDepth: number;
  nestedCardRowType: NestedCardRowType;
}

function UnplannableChildRow(props: UnplannableChildRowProps): JSX.Element {
  return (
    <div className={classNames('simpleDivTable-row', css(styles.childRow))} key={props.displayName}>
      <div
        className={classNames(
          'simpleDivTable-cell',
          getNestedCardRowStyles(props.nestedCardRowType, true, false),
          props.nestedCardRowType !== NestedCardRowType.NonNested && css(styles.nestedCardEmpty)
        )}
        style={{paddingLeft: `${getNestedRowPadding(props.nestedDepth)}px`}}
      />
      <div
        className={classNames(
          'simpleDivTable-cell',
          getNestedCardRowStyles(props.nestedCardRowType, false, true)
        )}
        style={{
          marginLeft: `${getNestedActionDropdownMargin(props.nestedDepth, props.nestedCardRowType)}px`,
        }}
      >
        <span className={css(styles.descriptionText)}>
          Migrate <strong>{props.displayName}</strong>
        </span>
      </div>
    </div>
  );
}

function getNestedCardRowStyles(
  nestedCardRowType: NestedCardRowType,
  isLeft: boolean,
  isRight: boolean
): Array<string> {
  if (nestedCardRowType === NestedCardRowType.NonNested) {
    return [];
  }

  const rowStyles = [css(styles.nestedCard)];
  if (
    nestedCardRowType === NestedCardRowType.HeaderAndFooter ||
    nestedCardRowType === NestedCardRowType.Header
  ) {
    rowStyles.push(css(styles.nestedCardTop));
  }
  if (
    nestedCardRowType === NestedCardRowType.HeaderAndFooter ||
    nestedCardRowType === NestedCardRowType.Footer
  ) {
    rowStyles.push(css(styles.nestedCardBottom));
  }
  if (isLeft) {
    rowStyles.push(css(styles.nestedCardLeft));
  }
  // Can't check !isLeft since a row can be both left and right
  if (isRight) {
    rowStyles.push(css(styles.nestedCardRight));
  }

  return rowStyles;
}

const styles = StyleSheet.create({
  ignoreRow: {
    color: GRAY_DARK,
  },
  childRow: {
    backgroundColor: GRAY_LIGHTER,
    border: 'none',
    paddingLeft: BASE_ROW_PADDING,
  },
  descriptionText: {
    marginRight: 6,
  },
  forcedAutoUpdateRow: {
    color: GRAY_DARK,
    'font-style': 'italic',
  },
  nestedCard: {
    backgroundColor: WHITE,
    // Extend background to fill row
    marginTop: '-10px',
    marginBottom: '-10px',
    paddingTop: '10px',
    paddingBottom: '10px',
  },
  nestedCardEmpty: {
    // total line-height of 28 divided by 2, padding is 10 on top and bottom
    marginTop: '-24px',
    marginBottom: '-24px',
    paddingTop: '24px',
    paddingBottom: '24px',
    // White background of empty row needs to fill width
    marginRight: '-13px',
    paddingRight: '13px',
  },
  nestedCardLeft: {
    borderLeft: `1px solid ${GRAY_LIGHT}`,
  },
  nestedCardRight: {
    borderRight: `1px solid ${GRAY_LIGHT}`,
    marginRight: BASE_ROW_PADDING,
  },
  nestedCardTop: {
    borderTop: `1px solid ${GRAY_LIGHT}`,
  },
  nestedCardBottom: {
    borderBottom: `1px solid ${GRAY_LIGHT}`,
  },
});
